import { useState } from 'react';
import { useUser } from '@clerk/clerk-react';
import { useApi } from '../utils/api';
import { sharedStyles } from './shared/sharedStyles.js';

/**
 * Test Dashboard - Simple component to test backend authentication
 */
export function TestDashboard() {
  const { user, isLoaded: isUserLoaded } = useUser();
  const { makeRequest } = useApi();
  const [apiResponse, setApiResponse] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const testBackendAuth = async () => {
    setLoading(true);
    setError(null);

    try {
      // Call backend API using the hook
      // Token is automatically injected by useApi
      const response = await makeRequest('users/me');
      setApiResponse(response);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  if (!isUserLoaded) {
    return <div style={styles.container}>Loading...</div>;
  }

  if (!user) {
    return (
      <div style={styles.container}>
        <h2>Please sign in to test the API</h2>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <div style={styles.card}>
        <h2 style={styles.title}>Test Dashboard</h2>

        <div style={styles.section}>
          <h3>Frontend (Clerk)</h3>
          <p><strong>Name:</strong> {user.fullName || user.firstName}</p>
          <p><strong>Email:</strong> {user.primaryEmailAddress?.emailAddress}</p>
          <p><strong>Clerk User ID:</strong> {user.id}</p>
        </div>

        <button
          onClick={testBackendAuth}
          disabled={loading}
          style={{
            ...styles.button,
            opacity: loading ? 0.5 : 1,
            cursor: loading ? 'not-allowed' : 'pointer'
          }}
        >
          {loading ? 'Testing...' : 'Test Backend Authentication'}
        </button>

        {error && (
          <div style={styles.error}>
            <strong>Error:</strong> {error}
          </div>
        )}

        {apiResponse && (
          <div style={styles.success}>
            <h3>Backend Response:</h3>
            <pre style={styles.code}>
              {JSON.stringify(apiResponse, null, 2)}
            </pre>
          </div>
        )}
      </div>
    </div>
  );
}

const styles = {
  container: {
    minHeight: '100vh',
    background: sharedStyles.colors.background,
    padding: '2rem',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  card: {
    background: sharedStyles.colors.glassBackground,
    backdropFilter: 'blur(20px)',
    border: '1px solid rgba(255, 255, 255, 0.3)',
    borderRadius: '20px',
    padding: '2rem',
    maxWidth: '600px',
    width: '100%',
    boxShadow: '0px 16px 64px rgba(15, 23, 42, 0.08)',
  },
  title: {
    color: sharedStyles.colors.accentBrown,
    marginBottom: '1.5rem',
  },
  section: {
    marginBottom: '1.5rem',
    padding: '1rem',
    background: 'rgba(234, 88, 12, 0.05)',
    borderRadius: '12px',
  },
  button: {
    background: sharedStyles.colors.primary,
    color: 'white',
    border: 'none',
    borderRadius: '12px',
    padding: '12px 24px',
    fontSize: '1rem',
    fontWeight: '600',
    width: '100%',
    marginTop: '1rem',
    transition: 'all 0.2s',
  },
  error: {
    marginTop: '1rem',
    padding: '1rem',
    background: sharedStyles.colors.errorLight,
    border: '1px solid rgba(239, 68, 68, 0.3)',
    borderRadius: '12px',
    color: sharedStyles.colors.error,
  },
  success: {
    marginTop: '1rem',
    padding: '1rem',
    background: sharedStyles.colors.successLight,
    border: '1px solid rgba(34, 197, 94, 0.3)',
    borderRadius: '12px',
  },
  code: {
    background: 'rgba(0, 0, 0, 0.05)',
    padding: '1rem',
    borderRadius: '8px',
    fontSize: '0.875rem',
    overflow: 'auto',
  },
};
